import React, { useMemo, useCallback } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useDynamicWidgetData } from '@/hooks/useDynamicWidgetData';
import { isWidgetScopeRefusal } from '@/services/dynamicWidgetQueryService';
import { DynamicVisualization, VisualizationClickEvent } from '../visualizations/DynamicVisualization';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { calculateDateRange } from '@/utils/timeframeUtils';
import { cn } from '@/lib/utils';
import type { DynamicWidgetConfig, GlobalDashboardFilters, DynamicWidgetFilters, DateRangeConfig, GlobalTimeframeOption, WidgetClickContext } from '@/types/dashboard';

interface DynamicWidgetProps {
  config: DynamicWidgetConfig;
  /** Widget ID for click context (DASH-24.2) */
  widgetId?: string;
  /** Widget title for drawer header (DASH-24.2) */
  widgetTitle?: string;
  globalFilters?: GlobalDashboardFilters;
  onEdit?: () => void;
  /** Callback when widget/segment is clicked for drill-down (DASH-24.2) */
  onWidgetClick?: (context: WidgetClickContext) => void;
}

/**
 * Convert GlobalTimeframeOption to DateRangeConfig format (DASH-10.2)
 * @param timeframe - Global timeframe option
 * @returns DateRangeConfig or undefined for "all time"
 */
function convertGlobalTimeframeToDateRange(
  timeframe: GlobalTimeframeOption
): DateRangeConfig | undefined {
  // "All time" = no filter
  if (timeframe.type === 'all') {
    return undefined;
  }

  // Custom date range
  if (timeframe.type === 'custom' && timeframe.customRange) {
    return {
      type: 'custom',
      customStart: timeframe.customRange.start.toISOString(),
      customEnd: timeframe.customRange.end.toISOString(),
    };
  }

  // Map global presets to DateRangeConfig types
  const typeMap: Record<string, DateRangeConfig['type']> = {
    'today': 'today',
    'yesterday': 'yesterday',
    'wtd': 'this_week',
    'mtd': 'this_month',
    'ytd': 'this_year',
    'last7': 'last_7_days',
    'last30': 'last_30_days',
  };

  const mappedType = typeMap[timeframe.type];
  if (mappedType) {
    return {
      type: mappedType,
    };
  }

  return undefined;
}

/**
 * Merge global filters with widget-specific filters (DASH-10.2)
 * Widget-specific filters ALWAYS take precedence over global filters (AC: 2, 8)
 * @param globalFilters - Global dashboard filters
 * @param widgetFilters - Widget-specific filters
 * @returns Merged filter configuration
 */
function mergeFilters(
  globalFilters: GlobalDashboardFilters | undefined,
  widgetFilters: DynamicWidgetFilters
): DynamicWidgetFilters {
  // No global filters? Return widget filters unchanged
  if (!globalFilters || !globalFilters.timeframe) {
    return widgetFilters;
  }

  // Widget has its own date range filter? Widget filter wins (no merge)
  if (widgetFilters.dateRange) {
    return widgetFilters;
  }

  // Convert global timeframe to DateRangeConfig
  const globalDateRange = convertGlobalTimeframeToDateRange(globalFilters.timeframe);

  // No date range from global filter (e.g., "all time")? Return widget filters unchanged
  if (!globalDateRange) {
    return widgetFilters;
  }

  // Merge: widget filters + global timeframe
  return {
    ...widgetFilters,
    dateRange: globalDateRange,
  };
}

/**
 * Universal Dynamic Widget Component (DASH-8.2, DASH-9.0, DASH-10.2, DASH-24.2)
 *
 * Content-only widget component that renders visualizations based on configuration.
 * Let Widget.tsx handle all chrome (Card, header, buttons, filter badge).
 *
 * Wrapped in React.memo for performance (DASH-8.7)
 * Refactored in DASH-9.0 for visual consistency with legacy widgets
 * DASH-24.2: Added click-through support for drill-down functionality
 *
 * @example
 * // Widget.tsx wraps this component and provides:
 * // - Card elevation and borders
 * // - Edit mode chrome (drag handle, settings, delete)
 * // - Title/subtitle display in view mode
 * // - Filter indicator badge
 * <DynamicWidget config={widgetConfig} onEdit={handleEdit} onWidgetClick={handleClick} />
 */
export const DynamicWidget = React.memo(function DynamicWidget({
  config,
  widgetId,
  widgetTitle,
  globalFilters,
  onEdit,
  onWidgetClick,
}: DynamicWidgetProps) {
  // DASH-10.2: Merge global filters with widget-specific filters
  // Widget-specific filters take precedence (AC: 2, 8)
  const effectiveFilters = useMemo(
    () => mergeFilters(globalFilters, config.filters),
    [globalFilters, config.filters]
  );

  // Create merged config with effective filters
  const mergedConfig = useMemo(
    () => ({ ...config, filters: effectiveFilters }),
    [config, effectiveFilters]
  );

  const { data, isLoading, error, refetch } = useDynamicWidgetData(mergedConfig);

  // DASH-24.2: Handle segment click and create WidgetClickContext
  const handleSegmentClick = useCallback(
    (event: VisualizationClickEvent) => {
      if (!onWidgetClick || !widgetId) return;

      const context: WidgetClickContext = {
        widgetId,
        widgetTitle: widgetTitle || config.display.title || 'Widget',
        clickedValue: event.value,
        clickedIndex: event.index,
        baseFilters: effectiveFilters,
        widgetConfig: config,
        clickedData: event.data,  // Pass full data for filter derivation (includes id for users)
      };

      onWidgetClick(context);
    },
    [onWidgetClick, widgetId, widgetTitle, config, effectiveFilters]
  );

  // DASH-20.3: Check if widget needs configuration (unmapped references)
  const needsConfiguration = (config as any)._needsConfiguration === true;
  const unmappedRefs = (config as any)._unmappedReferences;

  // Configuration warning banner
  if (needsConfiguration && unmappedRefs) {
    return (
      <div className="h-full w-full p-4 border-2 border-yellow-500 rounded-lg">
        <Alert className="bg-yellow-50 border-yellow-500">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">Configuration Required</AlertTitle>
          <AlertDescription className="text-yellow-700">
            This widget references resources that don't exist in this tenant.
            {onEdit && (
              <>
                {' '}
                <button
                  onClick={onEdit}
                  className="underline font-medium hover:text-yellow-900"
                >
                  Click here to configure
                </button>
                {' '}or contact your administrator.
              </>
            )}
            {!onEdit && ' Contact your administrator to configure this widget.'}
          </AlertDescription>
        </Alert>
        <div className="mt-4 text-sm text-muted-foreground">
          <p className="font-medium mb-2">Unmapped references:</p>
          <ul className="list-disc list-inside space-y-1">
            {unmappedRefs.pipelines && unmappedRefs.pipelines.length > 0 && (
              <li>{unmappedRefs.pipelines.length} pipeline{unmappedRefs.pipelines.length > 1 ? 's' : ''}</li>
            )}
            {unmappedRefs.stages && unmappedRefs.stages.length > 0 && (
              <li>{unmappedRefs.stages.length} stage{unmappedRefs.stages.length > 1 ? 's' : ''}</li>
            )}
            {unmappedRefs.checklistItems && unmappedRefs.checklistItems.length > 0 && (
              <li>{unmappedRefs.checklistItems.length} checklist item{unmappedRefs.checklistItems.length > 1 ? 's' : ''}</li>
            )}
            {unmappedRefs.users && unmappedRefs.users.length > 0 && (
              <li>User assignments cleared</li>
            )}
            {unmappedRefs.teams && unmappedRefs.teams.length > 0 && (
              <li>Team assignments cleared</li>
            )}
          </ul>
        </div>
      </div>
    );
  }

  // Loading state - simplified, no manual padding
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Skeleton className="h-32 w-32" />
      </div>
    );
  }

  /**
   * SUB-5.12 · AC3 · D88. A REFUSAL IS AN ANSWER, NOT A FAILURE.
   *
   * ⚠️ **THIS BRANCH MUST COME BEFORE THE ERROR BRANCH.** A widget that cannot
   * be computed across accounts — one grouped by stage, say, where the group
   * keys exist in exactly one account — is working correctly when it declines.
   * Rendering that as a red "Unable to Load Widget" with a Retry button tells
   * the user something broke and invites them to press a button that cannot
   * help, which is how a correct refusal becomes a support ticket.
   *
   * The reason string comes from `mergedFilters.ts` / `mergedMetrics.ts` /
   * `widgetScope.ts` and is written to be read by the person looking at it.
   */
  if (error && isWidgetScopeRefusal(error)) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-muted-foreground text-3xl mb-3" aria-hidden="true">🏢</div>
          <h3 className="text-base font-semibold text-foreground mb-2">
            Not shown across accounts
          </h3>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Error state - simplified, consistent with legacy widgets
  if (error) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Load Widget</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {error.message || 'Failed to fetch widget data. Please try again.'}
          </p>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state - only show when there's truly no data (not when count is 0)
  // Note: A count of 0 is valid data that should be displayed
  if (!data || (Array.isArray(data.data) && data.data.length === 0) || data.data === null || data.data === undefined) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-muted-foreground text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Data Found</h3>
          <p className="text-sm text-muted-foreground mb-2">
            No data matches your selected filters.
          </p>
          <p className="text-xs text-muted-foreground">
            Try adjusting your filters or date range.
          </p>
          {onEdit && (
            <Button onClick={onEdit} variant="outline" size="sm" className="mt-4">
              Adjust Filters
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Success: Render visualization only (content-only component)
  // Title/subtitle rendered by Widget.tsx wrapper, not here
  // No header, no footer, no timestamp - just the visualization
  // DASH-24.2: Pass click handler for drill-down
  return (
    <div className="h-full w-full">
      <DynamicVisualization
        type={config.visualization.type}
        data={data}
        config={config}
        onSegmentClick={onWidgetClick ? handleSegmentClick : undefined}
      />
    </div>
  );
});
